import { overlayTenantColours, type TenantBranding } from "@tenant-theme";

interface ThemeColors {
  dark: boolean;
  colors: Record<string, string>;
}

interface PaletteColors {
  primary: string;
  secondary: string;
  tertiary: string;
  background: string;
  foreground: string;
  navbar: string;
}

// Palette source of truth: metaship-frontend/utils/theme.js (light :1-52, dark
// :53-90) — mirrored, not imported cross-package (same convention as
// modules/portal/src/plugins/vuetify.ts `sharedColors`). Do not diverge from
// primary/secondary/tertiary/background/foreground/navbar without updating
// BOTH that file and this one.
const LIGHT_SOURCE: PaletteColors = {
  primary: "#649ECE",
  secondary: "#316B9B",
  tertiary: "#007d5c",
  background: "#E7EDF3",
  foreground: "#FFFFFF",
  navbar: "#27567C",
};
const DARK_SOURCE: PaletteColors = {
  primary: "#649ECE",
  secondary: "#316B9B",
  tertiary: "#007d5c",
  background: "#111927",
  foreground: "#1F2937",
  navbar: "#1F2937",
};

/** Vuetify-4 shaped `{ light, dark }` themes, tenant colours overlaid (Part D contract). */
export function buildTenantThemes(branding: TenantBranding | null): { light: ThemeColors; dark: ThemeColors } {
  const light: ThemeColors = { dark: false, colors: { ...LIGHT_SOURCE, accent: LIGHT_SOURCE.tertiary } };
  const dark: ThemeColors = { dark: true, colors: { ...DARK_SOURCE, accent: DARK_SOURCE.tertiary } };
  if (!branding) return { light, dark };
  return {
    light: { dark: false, colors: overlayTenantColours(light.colors, false, branding) },
    dark: { dark: true, colors: overlayTenantColours(dark.colors, true, branding) },
  };
}

function upsertMeta(name: string, content: string): void {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertFavicon(href: string): void {
  let el = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!el) {
    el = document.createElement("link");
    el.rel = "icon";
    document.head.appendChild(el);
  }
  el.href = href;
}

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

/** Only a strict `#rrggbb` value is trusted from tenant-supplied branding — anything else falls back to the MetaShip default. */
function safeColour(value: string | null | undefined, fallback: string): string {
  return value && HEX_COLOUR.test(value) ? value : fallback;
}

/**
 * Brand the DOM: title, favicon, theme-color, and `:root` CSS vars every
 * module's `@tenant-theme` overlay + the shell's own chrome read from.
 * Falls back to the MetaShip defaults when a colour is unset OR malformed
 * (branding contract: all keys nullable — Appendix, `branding` JSON; a bad
 * value here would otherwise land in a `style.setProperty()` call and/or a
 * Vuetify theme colour completely unvalidated).
 */
export function applyBranding(
  name: string,
  branding: TenantBranding | null,
  root: HTMLElement = document.documentElement,
): void {
  document.title = name;

  const defaultThemeIsDark = branding?.defaultTheme !== "light";
  const source = defaultThemeIsDark ? DARK_SOURCE : LIGHT_SOURCE;
  const colours = branding?.colours;

  const primary = safeColour(colours?.primary, source.primary);
  const secondary = safeColour(colours?.secondary, source.secondary);
  const tertiary = safeColour(colours?.tertiary, source.tertiary);
  const background = safeColour(colours?.background, source.background);
  const surface = safeColour(colours?.surface, source.foreground);
  const navbar = safeColour(colours?.navbar, source.navbar);

  root.style.setProperty("--tenant-primary", primary);
  root.style.setProperty("--tenant-secondary", secondary);
  root.style.setProperty("--tenant-tertiary", tertiary);
  root.style.setProperty("--tenant-background", background);
  root.style.setProperty("--tenant-surface", surface);
  root.style.setProperty("--tenant-navbar", navbar);

  upsertMeta("theme-color", navbar);
  if (branding?.iconUrl) upsertFavicon(branding.iconUrl);
}
